import { useEffect, useState } from "react";
import axios from "axios";
import { useI18n } from "@/i18n/I18nProvider";
import OrnamentDivider from "@/components/OrnamentDivider";
import { Instagram, ExternalLink, Heart, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mixed-grid layout: 12-col asymmetric placement
// Each post gets a layout slot — repeated when more than 6 posts.
const LAYOUTS = [
  { colSpan: 6, rowSpan: 2, aspect: "aspect-square" },   // big tile
  { colSpan: 3, rowSpan: 1, aspect: "aspect-square" },   // small
  { colSpan: 3, rowSpan: 1, aspect: "aspect-square" },   // small
  { colSpan: 3, rowSpan: 2, aspect: "aspect-[3/4]" },    // tall
  { colSpan: 3, rowSpan: 1, aspect: "aspect-square" },   // small
  { colSpan: 3, rowSpan: 1, aspect: "aspect-square" },   // small
];

// Placeholder posts shown until real Instagram is connected.
// Uses already-loaded local Shopify product images for visual coherence.
const PLACEHOLDER = [
  {
    id: "p1",
    media_url: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=900&q=80",
    permalink: "https://instagram.com/",
    caption: "King Kirchner · Vol. 02",
    like_count: 248,
    comments_count: 12,
    placeholder: true,
  },
  {
    id: "p2",
    media_url: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=600&q=80",
    permalink: "https://instagram.com/",
    caption: "Lion Crown Edition — drops soon.",
    like_count: 184,
    comments_count: 7,
    placeholder: true,
  },
  {
    id: "p3",
    media_url: "https://images.unsplash.com/photo-1556906781-9a412961c28c?auto=format&fit=crop&w=600&q=80",
    permalink: "https://instagram.com/",
    caption: "Backstage · WM 2026 Edition",
    like_count: 156,
    comments_count: 4,
    placeholder: true,
  },
  {
    id: "p4",
    media_url: "https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?auto=format&fit=crop&w=600&q=80",
    permalink: "https://instagram.com/",
    caption: "Royal Streetwear, no compromise.",
    like_count: 312,
    comments_count: 18,
    placeholder: true,
  },
  {
    id: "p5",
    media_url: "https://images.unsplash.com/photo-1554568218-0f1715e72254?auto=format&fit=crop&w=600&q=80",
    permalink: "https://instagram.com/",
    caption: "Hoodie season ⚜",
    like_count: 211,
    comments_count: 9,
    placeholder: true,
  },
  {
    id: "p6",
    media_url: "https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?auto=format&fit=crop&w=600&q=80",
    permalink: "https://instagram.com/",
    caption: "Black · Gold · Burgundy.",
    like_count: 178,
    comments_count: 6,
    placeholder: true,
  },
];

export default function InstagramFeed() {
  const { t, lang } = useI18n();
  const [posts, setPosts] = useState(PLACEHOLDER);
  const [handle, setHandle] = useState("@kirchero");
  const [isPlaceholder, setIsPlaceholder] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await axios.get(`${API}/instagram/feed`);
        if (cancelled) return;
        const data = res.data || {};
        if (Array.isArray(data.posts) && data.posts.length > 0) {
          setPosts(data.posts.slice(0, 12));
          setIsPlaceholder(false);
          if (data.username) setHandle("@" + data.username);
        }
      } catch {
        // Stay on placeholder — endpoint may return 503 until token is set.
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const copy =
    lang === "en"
      ? {
          chapter: "CH.04 — The Court Speaks",
          eyebrow: "Instagram",
          title: "Off-set, on-record.",
          subtitle:
            "Behind the scenes, raw drops, the everyday-heroes wearing the crown. Follow along.",
          follow: "Follow on Instagram",
          live: "Live Feed",
          coming: "Live feed coming soon",
        }
      : lang === "es"
      ? {
          chapter: "CH.04 — La Corte Habla",
          eyebrow: "Instagram",
          title: "Tras bambalinas, en directo.",
          subtitle:
            "Detrás de cámara, drops crudos, los héroes cotidianos llevando la corona. Síguenos.",
          follow: "Síguenos en Instagram",
          live: "Feed en directo",
          coming: "Feed en directo muy pronto",
        }
      : {
          chapter: "CH.04 — Hofgeflüster",
          eyebrow: "Instagram",
          title: "Hinter den Kulissen.",
          subtitle:
            "Backstage, rohe Drops, die Alltagshelden mit der Krone. Folge uns auf Instagram.",
          follow: "Auf Instagram folgen",
          live: "Live Feed",
          coming: "Live Feed kommt bald",
        };

  const igUrl = `https://instagram.com/${handle.replace(/^@/, "")}`;

  return (
    <section
      id="instagram"
      data-testid="instagram-section"
      className="relative py-28 sm:py-36 bg-[#050505] kh-section-cinematic overflow-hidden"
    >
      {/* Burgundy ambient glow bottom right */}
      <div
        className="absolute bottom-0 right-0 w-1/2 h-96 pointer-events-none opacity-30"
        style={{ background: "radial-gradient(ellipse at bottom right, rgba(123, 30, 46, 0.4), transparent 60%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        {/* Cinematic header */}
        <div className="mb-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-[#A93043]" />
              <span className="kh-chapter">{copy.chapter}</span>
              <span className="h-px w-8 bg-[#A93043]" />
            </div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-12 bg-[#D4AF37]" />
              <span className="kh-eyebrow">{copy.eyebrow}</span>
            </div>
            <h2
              data-testid="instagram-title"
              className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase text-white leading-[0.9]"
            >
              {copy.title.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="font-serif-luxury italic text-[#D4AF37] normal-case tracking-normal">
                {copy.title.split(" ").slice(-1)[0].replace(".", "")}.
              </span>
            </h2>
            <p className="mt-6 font-body text-base sm:text-lg text-white/65 max-w-xl leading-relaxed">
              {copy.subtitle}
            </p>
          </div>

          {/* Right: handle + follow CTA */}
          <div className="lg:col-span-5 flex flex-col items-start lg:items-end gap-4">
            <div className="flex items-center gap-3">
              <span className="relative flex h-2 w-2">
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${isPlaceholder ? "bg-[#A93043]" : "bg-emerald-400 animate-ping"}`} />
                <span className={`relative inline-flex rounded-full h-2 w-2 ${isPlaceholder ? "bg-[#A93043]" : "bg-emerald-400"}`} />
              </span>
              <span className="font-body text-[10px] tracking-[0.35em] uppercase text-white/55">
                {isPlaceholder ? copy.coming : copy.live}
              </span>
            </div>
            <a
              href={igUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="instagram-follow-cta"
              className="group flex items-center gap-3 px-6 py-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors font-body text-xs tracking-[0.25em] uppercase"
            >
              <Instagram className="w-4 h-4" />
              {copy.follow}
              <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
            <span className="font-serif-luxury italic text-sm text-white/40">
              {handle}
            </span>
          </div>
        </div>

        {/* Mixed grid */}
        <div
          data-testid="instagram-grid"
          className="grid grid-cols-12 gap-3 sm:gap-4 auto-rows-[140px] sm:auto-rows-[170px] lg:auto-rows-[200px]"
        >
          {posts.slice(0, 12).map((post, i) => {
            const layout = LAYOUTS[i % LAYOUTS.length];
            return (
              <a
                key={post.id || i}
                href={post.permalink || igUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`instagram-post-${i}`}
                className={`group relative overflow-hidden kh-belt-frame col-span-${layout.colSpan} row-span-${layout.rowSpan}`}
                style={{
                  gridColumn: `span ${layout.colSpan} / span ${layout.colSpan}`,
                  gridRow: `span ${layout.rowSpan} / span ${layout.rowSpan}`,
                }}
              >
                <img
                  src={post.media_url || post.thumbnail_url}
                  alt={(post.caption || "Instagram post").slice(0, 80)}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                />

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Item number */}
                <div className="absolute top-3 left-3 z-10 flex items-baseline gap-1">
                  <span className="font-serif-luxury italic text-[#B5651D] text-xs">No.</span>
                  <span className="font-display text-base text-[#D4AF37]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                {/* Instagram icon */}
                <div className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-black/40 backdrop-blur-sm border border-white/20 text-white opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:bg-[#D4AF37] group-hover:text-black group-hover:border-[#D4AF37]">
                  <Instagram className="w-3.5 h-3.5" />
                </div>

                {/* Stats on hover */}
                <div className="absolute bottom-3 left-3 right-3 z-10 flex items-center gap-4 text-white opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                  <div className="flex items-center gap-1.5">
                    <Heart className="w-3.5 h-3.5 fill-[#D4AF37] text-[#D4AF37]" />
                    <span className="font-body text-xs">{post.like_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MessageCircle className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="font-body text-xs">{post.comments_count || 0}</span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        {isPlaceholder && (
          <p className="mt-8 text-center font-serif-luxury italic text-sm text-white/40">
            * Vorschau-Layout — sobald Instagram-Account verbunden ist, erscheinen hier eure echten Posts.
          </p>
        )}

        <div className="mt-16">
          <OrnamentDivider />
        </div>
      </div>
    </section>
  );
}
